import { useState } from "react";
import { Loader2, X, KeyRound } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-[#0D0E11] border border-white/10 focus:border-amber-500/60 rounded-lg px-4 py-3 text-sm text-[#F3F4F6] outline-none transition-colors";

export default function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — use it next time you sign in.");
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-6" data-testid="change-password-modal">
      <div className="w-full max-w-md bg-[#151821] border border-white/10 rounded-2xl p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#6B7280] hover:text-[#F3F4F6] transition-colors" data-testid="change-password-close" aria-label="Close">
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 font-mono-tactical text-[11px] tracking-[0.25em] text-amber-500">
          <KeyRound size={14} /> SECURITY
        </div>
        <h2 className="mt-3 font-display font-bold text-xl text-[#F3F4F6] tracking-tight">Change password</h2>
        <p className="mt-1.5 text-sm text-[#9CA3AF]">Set a password you'll remember. Minimum 8 characters.</p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="change-password-form">
          <div>
            <label className="block font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280] mb-2">CURRENT PASSWORD</label>
            <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} data-testid="current-password-input" />
          </div>
          <div>
            <label className="block font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280] mb-2">NEW PASSWORD</label>
            <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} data-testid="new-password-input" />
          </div>
          <div>
            <label className="block font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280] mb-2">CONFIRM NEW PASSWORD</label>
            <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="confirm-password-input" />
          </div>
          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3" data-testid="change-password-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0D0E11] font-bold px-6 py-3.5 rounded-full transition-colors"
            data-testid="change-password-submit"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
